import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PMBandDef } from './types';

export interface BandInput {
  label: string;
  lower: number;
}

const MAX_BANDS = 10;

/**
 * In-report score-band configuration: viewers adjust the number of bands and
 * their floors; distributions recompute server-side against live data. The
 * lowest band's floor is always 0; each band's ceiling is derived from the
 * band above it.
 */
const BandEditor: React.FC<{
  current: PMBandDef[];
  defaults: PMBandDef[];
  isCustomized: boolean;
  onApply: (bands: BandInput[] | null) => void;
}> = ({ current, defaults, isCustomized, onApply }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<BandInput[]>([]);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(current.map((band) => ({ label: band.label, lower: band.lower })));
    setAnchor(event.currentTarget);
  };
  const close = () => setAnchor(null);

  const setRow = (index: number, patch: Partial<BandInput>) => {
    setDraft(draft.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const sorted = [...draft].sort((a, b) => b.lower - a.lower);
  const valid =
    draft.length > 0 &&
    draft.every((row) => row.label.trim() && row.lower >= 0 && row.lower <= 100) &&
    new Set(draft.map((row) => row.lower)).size === draft.length;

  const apply = () => {
    onApply(sorted.map((row, index) => ({
      label: row.label.trim(),
      lower: index === sorted.length - 1 ? 0 : row.lower,
    })));
    close();
  };

  const reset = () => {
    onApply(null);
    close();
  };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={open}>
        Bands{isCustomized ? ' *' : ''}
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: '20rem', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2">Score distribution bands</Typography>
          <Typography variant="caption" color="text.secondary">
            Each band runs from its floor up to the band above it; the lowest
            floor is always 0. Distributions update against current data.
          </Typography>
          {draft.map((row, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Label"
                value={row.label}
                onChange={(e) => setRow(index, { label: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Floor %"
                type="number"
                inputProps={{ min: 0, max: 100, step: 1 }}
                value={row.lower}
                onChange={(e) => setRow(index, { lower: Number(e.target.value) })}
                sx={{ width: '6.5rem' }}
              />
              <IconButton
                size="small"
                aria-label="Remove band"
                disabled={draft.length <= 1}
                onClick={() => setDraft(draft.filter((_, i) => i !== index))}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={draft.length >= MAX_BANDS}
            onClick={() => setDraft([...draft, { label: `Band ${draft.length + 1}`, lower: 0 }])}
          >
            Add band
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Button size="small" onClick={reset} disabled={!isCustomized}>
              Reset to default ({defaults.length} bands)
            </Button>
            <Button size="small" variant="contained" onClick={apply} disabled={!valid}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default BandEditor;
